import { useEffect, useRef, useState } from "react";

const MOBILE_QUERY = "(max-width: 767px)";
// Slightly below 1 so sub-pixel rounding still counts as fully visible.
const FULLY_VISIBLE = 0.99;

/**
 * Mirrors the last answer (or an info step's only button) into a sticky CTA on
 * mobile while it is not fully visible on screen (the user would have to
 * scroll to reach it). The button stays in place; the CTA hides once the whole
 * button is in view.
 */
export const useStickyLastOption = <T>(options: T[]) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [isLastOptionHidden, setIsLastOptionHidden] = useState(false);

  useEffect(() => {
    const lastOption = listRef.current?.lastElementChild;
    if (!lastOption || options.length === 0) return;

    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    let observer: IntersectionObserver | null = null;

    const observe = () => {
      observer?.disconnect();
      observer = null;

      if (!mobileQuery.matches) {
        setIsLastOptionHidden(false);
        return;
      }

      // A partly visible (cut off) answer still needs the sticky CTA.
      observer = new IntersectionObserver(
        ([entry]) =>
          setIsLastOptionHidden(entry.intersectionRatio < FULLY_VISIBLE),
        { threshold: [0, FULLY_VISIBLE] },
      );
      observer.observe(lastOption);
    };

    observe();
    mobileQuery.addEventListener("change", observe);

    return () => {
      observer?.disconnect();
      mobileQuery.removeEventListener("change", observe);
    };
  }, [options]);

  return {
    listRef,
    stickyOption:
      isLastOptionHidden && options.length > 0
        ? options[options.length - 1]
        : null,
  };
};
